/*
 * © 2017 Renishaw plc. All rights reserved.
 * This source file is the confidential property and copyright of Renishaw plc
 * Reproduction or transmission in whole or in part, in any form or
 * by any means, electronic, mechanical or otherwise, is prohibited
 * without the prior written consent of the copyright owner.
 */
import { rootActions } from "@/store";
import { create } from "@/presentation/Location";
import {
  put,
  call,
  delay,
  getContext,
  take,
  select,
  all,
  cancelled,
} from "typed-redux-saga";
import constants from "@/constants";
import { getGlobalConfigs } from "@/index";
import orderBy from "lodash/fp/orderBy";
import uniqBy from "lodash/fp/uniqBy";
import { getType } from "typesafe-actions";
import { CMSClientType } from "@/cms-api";
import { t } from "i18next";
import { SystemPermissions } from "@centralwebteam/narwhal";
import { selectUsername } from "@/store/selectors";
import { fetchFiles } from "@/store/sagas/shared";
import {
  POPUP_GROUP_JOB_SUMMARY,
  JOB_SUMMARY_POPUP_ID_PREFIX,
} from "@/components/JobSummary/Popup.constants";

export function* fastCoreDataLoopSaga() {
  const config = yield* call(() => getGlobalConfigs());
  while (true) {
    yield* delay(config.refreshRateFastInMs);
    try {
      if (!window.location.href.includes("process-updates"))
        yield* getFastCoreDataSaga();
    } catch (error) {
      console.log(error);
    }
  }
}

export function* slowCoreDataLoopSaga() {
  const config = yield* call(() => getGlobalConfigs());
  while (true) {
    yield* delay(config.refreshRateCoreInMS);
    try {
      yield* getSlowCoreDataSaga();
    } catch (error) {
      console.log(error);
    }
  }
}

/** Core data we need refreshed often (e.g. every 30s) */
export function* getFastCoreDataSaga() {
  try {
    yield* all([getMachinesSaga(), getSystemPermissionSaga()]);
  } catch (error) {
    console.warn(`Core data (fast): ${error}`);
  }
}

/** Core data we need refreshed less often (e.g. every 5 minutes) */
export function* getSlowCoreDataSaga() {
  try {
    yield* all([
      getMachineTypesSaga(),
      getLocationsSaga(),
      getPreferencesSaga(),
    ]);
  } catch (error) {
    console.warn(`Core data (slow): ${error}`);
  }
}

/** Refresh both fast and slow core data */
export function* getAllCoreDataSaga() {
  yield* all([getFastCoreDataSaga(), getSlowCoreDataSaga()]);
}

export function* dataLoadingPerformanceNotificationWatcher() {
  const config = yield* call(() => getGlobalConfigs());
  while (
    yield* take([
      getType(
        rootActions.machineAnalysis.timelineStaticDataWarningLimitBreached
      ),
      getType(rootActions.jobPerformance.jobSummariesDataWarningLimitBreached),
    ])
  ) {
    yield* put(rootActions.global.performanceNotificationRaised());
    yield* delay(config.performanceNotificationLockedTimeInMS);
  }
}

function* getMachinesSaga() {
  const config = yield* call(() => getGlobalConfigs());
  const client: CMSClientType = yield getContext("client");
  const machines = yield* call(() =>
    client.machines
      .all({
        query: {
          take: config.maxTake,
        },
      })
      .promise.then((res) => uniqBy("id", res))
  );
  yield* put(rootActions.global.setMachines(orderBy("name", "asc", machines)));
}

function* getMachineTypesSaga() {
  const client: CMSClientType = yield getContext("client");
  const machineTypes = yield* call(() => client.machines.types().promise);
  yield* put(rootActions.global.setMachineTypes(machineTypes));
}

function* getLocationsSaga() {
  const client: CMSClientType = yield getContext("client");
  const locations = yield* call(() =>
    client.locations.flat().promise.then((res) =>
      uniqBy("id", create(res)).concat({
        ...constants.unassignedMachinesLocation,
        name: t("label-Unassigned Machines"),
        trail: t("label-Unassigned Machines"),
      })
    )
  );
  yield* put(rootActions.global.setLocations(locations));
}

function* getSystemPermissionSaga() {
  try {
    const users = yield* select((s) => s.manageUsers.users);
    let username: string | undefined = yield* select(selectUsername);
    username = username.split("\\").pop();
    const user = users.find(
      (u: any) => u.email.toLowerCase() === username?.toLowerCase()
    );
    if (username && user) {
      const client: CMSClientType = yield getContext("client");

      const userData = yield* call(() => client.users.byId(user.id).promise);
      const permissions =
        userData && userData.permissions
          ? (Object.keys(userData.permissions) as SystemPermissions[]).filter(
              (key) => userData.permissions![key]
            )
          : [];
      const locationEntityPermissions =
        userData?.locationEntityPermissions ?? [];
      yield* put(
        rootActions.session.setUserPermission({
          username,
          permissions,
          locationEntityPermissions,
        })
      );
    }
  } catch (e) {
    console.log(e);
  }
}

function* getPreferencesSaga() {
  try {
    const client: CMSClientType = yield getContext("client");
    const preferences = yield* call(() => client.preferences.all().promise);
    yield* put(rootActions.global.setPreferences(preferences));
  } catch (e) {
    console.log(e);
  }
}

// Fetch details for file Info Ids
export function* fetchFilesDetailsForFileInfoIds({
  payload: { jobId, fileInfoIds },
}: any) {
  const client: CMSClientType = yield getContext("client");
  const results = [];
  let response: any = {};
  try {
    for (const fileInfoId of fileInfoIds) {
      try {
        response = yield* call(
          () => client.files.filesDetailsByFileInfoId(fileInfoId).promise,
          fileInfoId
        );
        results.push(response);
      } catch (error) {
        console.error(error);
      }
    }
    yield* put(
      rootActions.global.setFilesDetailsForSelectedJob({
        jobId,
        files: results.map((obj) => obj[0]),
      })
    );
  } catch (error) {
    console.error(error);
  }
}

// Download the file for selected file Id
export function* downloadFileForSelectedFileId({
  payload: {
    jobId,
    fileId,
    fileName,
    downloadFilename,
    fileExtension,
    extension,
  },
}: any) {
  // Ignore repeat clicks for a file that is already downloading. `takeEvery` would
  // otherwise happily start a second, duplicate request for the same file.
  const existingState = yield* select(
    (s) => s.global.files[jobId]?.downloadState?.[fileId]
  );
  if (existingState === "downloading") {
    return;
  }

  const client: CMSClientType = yield getContext("client");
  yield* put(
    rootActions.global.setDownloadFileState({
      jobId,
      fileId,
      state: "downloading",
    })
  );
  try {
    const response = yield* call(
      () => client.files.downloadFileByFileInfoId(fileId).promise
    );
    const fileNameToDownload = downloadFilename ?? fileName;
    const fileType = fileExtension ?? extension;

    const blob = new Blob([response]);
    const link = document.createElement("a");
    const objectUrl = URL.createObjectURL(blob);
    link.href = objectUrl;
    link.download =
      fileType !== undefined
        ? fileNameToDownload.replace(/\.[^.]+$/, "") + "." + fileType
        : fileNameToDownload;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    // Release the blob URL once the browser has had a chance to start the
    // download. URL.revokeObjectURL is safe to call after click() has been
    // dispatched; we defer it slightly so the navigation isn't cancelled in
    // some browsers.
    setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
    yield* put(
      rootActions.global.setDownloadFileState({
        jobId,
        fileId,
        state: "success",
      })
    );
    return response;
  } catch (error) {
    console.error(error);
    yield* put(
      rootActions.global.setDownloadFileState({ jobId, fileId, state: "fail" })
    );
  } finally {
    // A cancelled task (e.g. the enclosing `race` is torn down by a session change)
    // would otherwise skip both terminal dispatches above and strand the row on
    // "downloading". Reset to "initial" so the spinner clears; a cancellation is not
    // a download failure the user needs to be warned about.
    if (yield* cancelled()) {
      yield* put(
        rootActions.global.setDownloadFileState({
          jobId,
          fileId,
          state: "initial",
        })
      );
    }
  }
}

// Refetch the files list for a JobSummary popup whenever it is opened.
export function* refreshJobSummaryFilesOnPopupOpened(
  action: ReturnType<typeof rootActions.global.popupUpdated>
) {
  const { id, show, group } = action.payload;
  if (show !== true || group !== POPUP_GROUP_JOB_SUMMARY) return;
  if (!id.startsWith(JOB_SUMMARY_POPUP_ID_PREFIX)) return;
  const jobId = id.slice(JOB_SUMMARY_POPUP_ID_PREFIX.length);
  if (!jobId) return;
  const filesInfoForSelectedJob = yield* fetchFiles({ jobId });
  yield* put(
    rootActions.global.jobSummaryPopupFilesRefreshed({
      jobId,
      filesInfoForSelectedJob: filesInfoForSelectedJob ?? [],
    })
  );
}
